import CryptoJS from 'crypto-js';

// 브라우저 호환 해시 함수
const sha256 = async (message: string): Promise<string> => {
  try {
    // crypto-js를 사용한 SHA-256 해시 생성
    const hash = CryptoJS.SHA256(message);
    return hash.toString(CryptoJS.enc.Hex);
  } catch (error) {
    console.error('SHA-256 해시 생성 중 오류:', error);
    // 폴백: 간단한 해시 함수 사용
    return simpleHash(message);
  }
};

// 폴백용 간단한 해시 함수
const simpleHash = (message: string): string => {
  let hash = 0;
  if (message.length === 0) return hash.toString(16).padStart(16, '0');
  
  for (let i = 0; i < message.length; i++) {
    const char = message.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 32비트 정수로 변환
  }
  
  return Math.abs(hash).toString(16).padStart(16, '0');
};

// 시크릿 키 (실제 운영에서는 환경변수로 관리해야 함)
const SECRET_KEY = 'omniscient-readers-viewpoint-secret-key-2025';

/**
 * 현재 시간을 기준으로 1시간 단위의 타임스탬프를 생성 (YYYYMMDDHH 형태)
 * 예: 2024-01-15 14:30:00 -> 2024011514
 */
export const getCurrentHourTimestamp = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hour = String(now.getHours()).padStart(2, '0');
  
  return `${year}${month}${day}${hour}`;
};

/**
 * 타임스탬프를 기반으로 해시값 생성
 */
export const generateHash = async (timestamp: string): Promise<string> => {
  const data = `${timestamp}-${SECRET_KEY}`;
  const hash = await sha256(data);
  return hash.substring(0, 16);
};

/**
 * 1시간마다 유효한 URL 파라미터 생성
 * @returns {object} { timestamp, hash, isValid }
 */
export const generateHourlyParams = async (): Promise<{ timestamp: string; hash: string }> => {
  const timestamp = getCurrentHourTimestamp();
  const hash = await generateHash(timestamp);
  
  return {
    timestamp,
    hash
  };
};

/**
 * 두 타임스탬프 간의 시간 차이를 계산 (시간 단위)
 * @param timestamp1 첫 번째 타임스탬프 (YYYYMMDDHH)
 * @param timestamp2 두 번째 타임스탬프 (YYYYMMDDHH)
 * @returns {number} 시간 차이 (시간)
 */
const getTimeDifference = (timestamp1: string, timestamp2: string): number => {
  try {
    // 타임스탬프 길이 검증
    if (timestamp1.length !== 10 || timestamp2.length !== 10) {
      return Infinity;
    }
    
    const year1 = parseInt(timestamp1.substring(0, 4));
    const month1 = parseInt(timestamp1.substring(4, 6)) - 1; // 월은 0부터 시작
    const day1 = parseInt(timestamp1.substring(6, 8));
    const hour1 = parseInt(timestamp1.substring(8, 10));
    
    const year2 = parseInt(timestamp2.substring(0, 4));
    const month2 = parseInt(timestamp2.substring(4, 6)) - 1;
    const day2 = parseInt(timestamp2.substring(6, 8));
    const hour2 = parseInt(timestamp2.substring(8, 10));
    
    // 유효한 날짜인지 확인
    const date1 = new Date(year1, month1, day1, hour1);
    const date2 = new Date(year2, month2, day2, hour2);
    
    // 날짜가 유효하지 않으면 Infinity 반환
    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
      return Infinity;
    }
    
    const diffMs = Math.abs(date2.getTime() - date1.getTime());
    const diffHours = diffMs / (1000 * 60 * 60); // 시간 단위로 변환
    
    // 현재 시간을 기준으로 더 정확한 계산
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    // 타임스탬프의 시간과 현재 시간의 차이를 더 정확히 계산
    let adjustedDiff = diffHours;
    
    // 만약 현재 시간이 타임스탬프 시간보다 늦다면, 분 단위까지 고려
    if (hour2 > hour1 || (hour2 === hour1 && currentMinute > 0)) {
      adjustedDiff = diffHours + (currentMinute / 60);
    }
    
    
    return adjustedDiff;
  } catch (error) {
    console.error('시간 차이 계산 중 오류:', error);
    return Infinity;
  }
};

/**
 * URL 파라미터의 유효성을 검증
 * @param timestamp URL에서 받은 타임스탬프 (YYYYMMDDHH)
 * @param hash URL에서 받은 해시값
 * @returns {boolean} 유효성 여부
 */
export const validateHourlyParams = async (timestamp: string, hash: string): Promise<boolean> => {
  try {
    
    // 1. 타임스탬프 형식 검증 (10자리 숫자)
    if (!/^\d{10}$/.test(timestamp)) {
      return false;
    }
    
    // 2. 타임스탬프가 현재 시간과 2시간 이내인지 확인
    const currentTimestamp = getCurrentHourTimestamp();
    
    const timeDiff = getTimeDifference(timestamp, currentTimestamp);
    
    // 2시간 이내인지 확인
    if (timeDiff > 2) {
      return false;
    }
    
    // 3. 해시값이 올바른지 확인
    const expectedHash = await generateHash(timestamp);
    
    const isValidHash = hash === expectedHash;
    
    return isValidHash;
    
  } catch (error) {
    console.error('URL 파라미터 검증 중 오류:', error);
    return false;
  }
};

/**
 * URL 파라미터를 문자열로 생성
 * @returns {string} URL 파라미터 문자열 (예: "?t=2024011514&h=a1b2c3d4e5f6g7h8")
 */
export const generateHourlyUrlParams = async (): Promise<string> => {
  const { timestamp, hash } = await generateHourlyParams();
  return `?t=${timestamp}&h=${hash}`;
};

/**
 * URL 파라미터를 파싱하여 검증
 * @param searchParams URLSearchParams 객체
 * @returns {boolean} 유효성 여부
 */
export const parseAndValidateParams = async (searchParams: URLSearchParams): Promise<boolean> => {
  const timestamp = searchParams.get('t');
  const hash = searchParams.get('h');
  
  if (!timestamp || !hash) {
    return false;
  }
  
  return await validateHourlyParams(timestamp, hash);
};

/**
 * 다음 유효 시간까지 남은 시간을 계산 (분 단위)
 * @returns {number} 남은 시간 (분)
 */
export const getTimeUntilNextHour = (): number => {
  const now = new Date();
  const nextHour = new Date(now);
  nextHour.setHours(now.getHours() + 1, 0, 0, 0);
  
  const diffMs = nextHour.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60)); // 분 단위로 변환
};
